import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Accept': 'application/json',
  },
});

const containsFile = (data) => {
  if (data instanceof FormData) {
    for (let pair of data.entries()) {
      if (pair[1] instanceof File) {
        return true;
      }
    }
  }
  return false;
};

const makeRequest = (method, url, data = null, params = null) => {
  const config = {};

  if (data) {
    config.headers = {
      'Content-Type': containsFile(data) ? 'multipart/form-data' : 'application/json'
    };

    if (method === 'put' && data instanceof FormData) {
      method = 'post';
    }
  }

  if (params) {
    config.params = params;
  }

  return api({ method, url, data, params, ...config });
};

api.auth = {
  login: (credentials) => makeRequest('post', '/auth/login', credentials),
  logout: () => makeRequest('post', '/auth/logout'),
  register: (formData) => makeRequest('post', '/auth/register', formData),
  profile: () => makeRequest('get', '/auth/profile'),
  updateProfile: (formData) => makeRequest('put', '/auth/profile', formData),
}

api.user = {
  getUserData: async (username, contentType = 'twuts') => {
    try {
      const profileEndpoint = `/${username}/profile`;
      const contentEndpoint = contentType === 'replies' 
        ? `/${username}/replies` 
        : `/${username}`;
      
      const [profileResponse, contentResponse] = await Promise.all([
        makeRequest('get', profileEndpoint),
        makeRequest('get', contentEndpoint)
      ]);

      return {
        user: profileResponse,
        [contentType]: contentResponse
      };
    } catch (error) {
      throw error;
    }
  },
  publicProfile: (username) => makeRequest('get', `/${username}/profile`),
  toggleFollow: (username) => makeRequest('post', `/${username}/toggle-follow`),
  profileWithTwuts: (username) => api.user.getUserData(username, 'twuts'),
  getRepliesWithParents: (username) => api.user.getUserData(username, 'replies'),
}

api.twut = {
  list: (params = null) => makeRequest('get', '/twuts/show', null, params),
  listFollowing: (params = null) => makeRequest('get', '/twuts/following', null, params),
  listFromUser: (username) => makeRequest('get', `/${username}`),
  listThread: (id, sortBy = 'newest') => makeRequest('get', `/twuts/show/${id}`, null, { sort_by: sortBy }),
  authList: () => makeRequest('get', '/auth/twuts/show'),
  authGet: (id) => makeRequest('get', `/auth/twuts/${id}`),
  create: (data) => makeRequest('post', '/twuts', data),
  delete: (id) => makeRequest('delete', `/twuts/${id}`),
  update: (id, data) => makeRequest('put', `/twuts/${id}`, data),
  toggleLike: (twutId) => makeRequest('post', `/twuts/${twutId}/toggle-like`),
  reply: (parentId, data) => makeRequest('post', `/twuts/${parentId}/reply`, data),
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  response => response.data,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
    }

    if (error.response) {
      console.error('Response error:', error.response.data);
    } else if (error.request) {
      console.error('Request error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default api;
